'use client';

import { useState } from 'react';
import { useFirestore } from '@/firebase';
import { doc, setDoc, Timestamp } from 'firebase/firestore';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface PrivacySettingsDialogProps {
  open: boolean;
  userId: string;
  privacySettingsId: string; // Now uses privacySettingsId instead of membershipId
  onComplete: () => void;
}

/**
 * Privacy Settings Dialog Component
 * 
 * Allows users to configure their privacy settings for the member directory.
 * Settings are stored in the privacySettings collection (v5 schema).
 */
export function PrivacySettingsDialog({ 
  open, 
  userId, 
  privacySettingsId, 
  onComplete 
}: PrivacySettingsDialogProps) {
  const firestore = useFirestore();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [settings, setSettings] = useState({
    showEmail: false,
    showPhone: false,
    showAddress: false,
    showSeasonalAddress: false,
  });

  const handleSave = async () => {
    setIsSubmitting(true);
    
    try {
      // Save to privacySettings collection (v5 schema)
      const privacyRef = doc(firestore, 'privacySettings', privacySettingsId);
      await setDoc(privacyRef, {
        showEmail: settings.showEmail,
        showPhone: settings.showPhone,
        showAddress: settings.showAddress,
        showSeasonalAddress: settings.showSeasonalAddress,
        updatedAt: Timestamp.now(),
        updatedBy: userId,
      }, { merge: true });

      // Mark privacy settings as configured in user profile
      const userRef = doc(firestore, 'users', userId);
      await setDoc(userRef, {
        privacySettingsConfigured: true,
        updatedAt: Timestamp.now().toDate(),
      }, { merge: true });

      toast({
        title: 'Privacy Settings Saved',
        description: 'Your privacy preferences have been saved. You can change them anytime in your profile.',
      });

      onComplete();
    } catch (error: any) {
      console.error('Failed to save privacy settings:', error);
      toast({
        variant: 'destructive',
        title: 'Save Failed',
        description: error.message || 'Could not save your privacy settings. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-[525px]" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Privacy Settings
          </DialogTitle>
          <DialogDescription>
            Choose what contact information to share in the member directory
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <Alert>
            <AlertDescription className="text-sm">
              <strong>Your Privacy Matters:</strong> By default, all your contact information is private. 
              Only check the boxes below if you want to share specific information with other club members 
              in the member directory.
            </AlertDescription>
          </Alert>

          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="showEmail"
                checked={settings.showEmail}
                onCheckedChange={(checked) => 
                  setSettings(prev => ({ ...prev, showEmail: !!checked }))
                }
              />
              <div className="grid gap-1.5 leading-none">
                <Label
                  htmlFor="showEmail"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Show my email address
                </Label>
                <p className="text-sm text-muted-foreground">
                  Other members will be able to see your email address in the directory
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Checkbox
                id="showPhone"
                checked={settings.showPhone}
                onCheckedChange={(checked) => 
                  setSettings(prev => ({ ...prev, showPhone: !!checked }))
                }
              />
              <div className="grid gap-1.5 leading-none">
                <Label
                  htmlFor="showPhone"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Show my phone number
                </Label>
                <p className="text-sm text-muted-foreground">
                  Other members will be able to see your phone number in the directory
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Checkbox
                id="showAddress"
                checked={settings.showAddress}
                onCheckedChange={(checked) => 
                  setSettings(prev => ({ ...prev, showAddress: !!checked }))
                }
              />
              <div className="grid gap-1.5 leading-none">
                <Label
                  htmlFor="showAddress"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Show my mailing address
                </Label>
                <p className="text-sm text-muted-foreground">
                  Other members will be able to see your mailing address in the directory
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Checkbox
                id="showSeasonalAddress"
                checked={settings.showSeasonalAddress}
                onCheckedChange={(checked) =>
                  setSettings(prev => ({ ...prev, showSeasonalAddress: !!checked }))
                }
              />
              <div className="grid gap-1.5 leading-none">
                <Label
                  htmlFor="showSeasonalAddress"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Show Seasonal Address
                </Label>
                <p className="text-sm text-muted-foreground">
                  Allow other members to see your seasonal address
                </p>
              </div>
            </div>
          </div>

          <Alert>
            <AlertDescription className="text-xs">
              <strong>Note:</strong> Club officers and administrators can view all contact information 
              regardless of these settings to support club operations. You can change these settings 
              at any time from your profile page.
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button onClick={handleSave} disabled={isSubmitting} className="w-full sm:w-auto">
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Privacy Settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
